import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { DayPicker, useNavigation } from "react-day-picker";
import { createPortal } from "react-dom";
import {
  emptyWeekdays,
  WEEKDAYS,
  weekdayStateFromPython,
  weekdayStateToPython,
} from "./recurring-weekday-encoding";

const SCHEDULE_SINGLE = "single_day";
const SCHEDULE_RANGE = "date_range";
const SCHEDULE_RECURRING = "recurring";

/** Recurring end mode — POST uses `recurring_ends: "never"` or `recurring_until_date` only */
const RECURRING_ENDS_NEVER = "never";
const RECURRING_ENDS_ON_DATE = "on_date";

const TIME_FULL_DAY = "full_day";
const TIME_SPECIFIC = "specific_time";

/** POST body `action`: coach_wide | event_type_batch | single_event — UI is coach-wide only for now */
const BLACKOUT_ACTION_COACH_WIDE = "coach_wide";

/** Approximate popover size for viewport clamping / flip above */
const POPOVER_EST_HEIGHT = 320;
const POPOVER_EST_WIDTH = 296;

const WEEKDAY_FULL_NAMES = {
  mon: "Monday",
  tue: "Tuesday",
  wed: "Wednesday",
  thu: "Thursday",
  fri: "Friday",
  sat: "Saturday",
  sun: "Sunday",
};

function parseDateField(s) {
  if (!s || typeof s !== "string") return undefined;
  const day = s.trim().slice(0, 10);
  const m = day.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return undefined;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 0, 0, 0, 0);
}

/**
 * One calendar date for API/labels: if the instant is **exactly UTC midnight** for a
 * calendar day (common for `yyyy-mm-dd` / DayPicker Z), use **UTC** Y-M-D. Otherwise
 * use **local** Y-M-D (local-midnight dates from the picker).
 */
function calendarDateToYyyyMmDd(d) {
  if (!d || !(d instanceof Date) || Number.isNaN(d.getTime())) {
    return "";
  }
  const uy = d.getUTCFullYear();
  const um = d.getUTCMonth();
  const ud = d.getUTCDate();
  const utcMidnightMs = Date.UTC(uy, um, ud);
  const isExactlyUtcMidnight =
    d.getTime() === utcMidnightMs &&
    d.getUTCHours() === 0 &&
    d.getUTCMinutes() === 0 &&
    d.getUTCSeconds() === 0 &&
    d.getUTCMilliseconds() === 0;
  if (isExactlyUtcMidnight) {
    return `${uy}-${String(um + 1).padStart(2, "0")}-${String(ud).padStart(2, "0")}`;
  }
  const y = d.getFullYear();
  const m = d.getMonth() + 1;
  const day = d.getDate();
  return `${y}-${String(m).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

/** Stable Date at local noon from a calendar Y-M-D (avoids DST edge cases). */
function dateAtNoonFromYyyyMmDd(ymd) {
  if (!ymd || typeof ymd !== "string") return undefined;
  const m = ymd.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return undefined;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12, 0, 0, 0);
}

function normalizeCalendarDateForBlackout(d) {
  const ymd = calendarDateToYyyyMmDd(d);
  if (!ymd) {
    return d;
  }
  return dateAtNoonFromYyyyMmDd(ymd);
}

function mapTypeLabelToScheduleType(label) {
  if (!label) return SCHEDULE_SINGLE;
  const s = String(label).toLowerCase();
  if (s.includes("recurring")) return SCHEDULE_RECURRING;
  if (s.includes("range")) return SCHEDULE_RANGE;
  if (s.includes("single")) return SCHEDULE_SINGLE;
  return SCHEDULE_SINGLE;
}

function buildBlackoutTimeOptions() {
  const out = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 15) {
      const hh = String(h).padStart(2, "0");
      const mm = String(m).padStart(2, "0");
      const value = `${hh}:${mm}`;
      const h12 = h % 12 || 12;
      const ap = h < 12 ? "AM" : "PM";
      const label = `${h12}:${mm} ${ap}`;
      out.push({ value, label });
    }
  }
  return out;
}

const BLACKOUT_TIME_OPTIONS = buildBlackoutTimeOptions();

function formatHHmm12(value) {
  if (!value || typeof value !== "string") return "";
  const m = value.match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return value;
  const h = Number(m[1]);
  const min = m[2];
  const ap = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 || 12;
  return `${h12}:${min} ${ap}`;
}

function joinEnglishList(items) {
  if (items.length === 0) return "";
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`;
}

function normalizeHHmm(s) {
  if (!s || typeof s !== "string") return "";
  const m = s.trim().match(/^(\d{1,2}):(\d{2})/);
  if (!m) return "";
  const h = Math.min(23, Math.max(0, Number(m[1])));
  return `${String(h).padStart(2, "0")}:${m[2]}`;
}

function BlackoutCalendarCaption(props) {
  const { goToMonth, nextMonth, previousMonth } = useNavigation();
  const displayMonth = props?.displayMonth ?? new Date();
  const monthLabel = displayMonth.toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });
  return (
    <div className="create-class-modal-calendar-caption">
      <button
        type="button"
        onClick={() => previousMonth && goToMonth(previousMonth)}
        className="create-class-modal-calendar-nav"
        aria-label="Previous month"
      >
        <img src="/img/new-icons/arrow-left.svg" alt="" />
      </button>
      <span className="create-class-modal-calendar-title">{monthLabel}</span>
      <button
        type="button"
        onClick={() => nextMonth && goToMonth(nextMonth)}
        className="create-class-modal-calendar-nav"
        aria-label="Next month"
      >
        <img src="/img/new-icons/arrow-right.svg" alt="" />
      </button>
    </div>
  );
}

const CreateBlackoutModal = ({ isOpen, onClose, onConfirm, initialRecord }) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [scheduleType, setScheduleType] = useState(SCHEDULE_SINGLE);

  const [singleDayDate, setSingleDayDate] = useState(undefined);
  const [rangeStartDate, setRangeStartDate] = useState(undefined);
  const [rangeEndDate, setRangeEndDate] = useState(undefined);
  const [weekdays, setWeekdays] = useState(emptyWeekdays);
  const [recurringEnds, setRecurringEnds] = useState(RECURRING_ENDS_NEVER);
  const [recurringUntilDate, setRecurringUntilDate] = useState(undefined);

  const [timeMode, setTimeMode] = useState(TIME_FULL_DAY);
  const [startTime, setStartTime] = useState("11:00");
  const [endTime, setEndTime] = useState("13:00");

  const [openDatePicker, setOpenDatePicker] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const singlePickerRef = useRef(null);
  const rangeStartPickerRef = useRef(null);
  const rangeEndPickerRef = useRef(null);
  const recurringUntilPickerRef = useRef(null);
  const calendarPopoverRef = useRef(null);
  const [calendarPopoverPos, setCalendarPopoverPos] = useState({
    top: 0,
    left: 0,
  });

  const getPickerRefForKey = useCallback((key) => {
    switch (key) {
      case "single":
        return singlePickerRef;
      case "rangeStart":
        return rangeStartPickerRef;
      case "rangeEnd":
        return rangeEndPickerRef;
      case "recurringUntil":
        return recurringUntilPickerRef;
      default:
        return null;
    }
  }, []);

  const handleClose = (e) => {
    e?.preventDefault?.();
    onClose();
  };

  const formatDate = (d) =>
    d
      ? d.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        })
      : "";
  /** YYYY-MM-DD for API — same rules as `calendarDateToYyyyMmDd` (UTC Z vs local). */
  const toYYYYMMDD = (d) => calendarDateToYyyyMmDd(d);

  const toggleWeekday = (key) => {
    setWeekdays((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const updateCalendarPopoverPosition = useCallback(() => {
    if (!openDatePicker) return;
    const anchorRef = getPickerRefForKey(openDatePicker);
    if (!anchorRef?.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    const gap = 4;
    const pad = 8;
    const left = Math.max(
      pad,
      Math.min(rect.left, window.innerWidth - POPOVER_EST_WIDTH - pad)
    );
    let top = rect.bottom + gap;
    if (
      rect.bottom + POPOVER_EST_HEIGHT > window.innerHeight - pad &&
      rect.top > POPOVER_EST_HEIGHT + pad
    ) {
      top = rect.top - POPOVER_EST_HEIGHT - gap;
    }
    setCalendarPopoverPos({ top, left });
  }, [openDatePicker, getPickerRefForKey]);

  useLayoutEffect(() => {
    if (!openDatePicker) return;
    updateCalendarPopoverPosition();
  }, [openDatePicker, updateCalendarPopoverPosition]);

  useEffect(() => {
    if (!openDatePicker) return;
    const onScrollOrResize = () => updateCalendarPopoverPosition();
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [openDatePicker, updateCalendarPopoverPosition]);

  useEffect(() => {
    if (!openDatePicker) return;
    const handleClickOutside = (e) => {
      const anchorRef = getPickerRefForKey(openDatePicker);
      const inAnchor = anchorRef?.current?.contains(e.target);
      const inPopover = calendarPopoverRef.current?.contains(e.target);
      if (!inAnchor && !inPopover) {
        setOpenDatePicker(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openDatePicker, getPickerRefForKey]);

  useEffect(() => {
    if (!isOpen) return;
    const id = requestAnimationFrame(() => {
      if (initialRecord && typeof initialRecord === "object") {
        const r = initialRecord;
        setName(String(r.name ?? r.title ?? "").trim());
        setDescription(String(r.description ?? "").trim());
        setScheduleType(
          r.schedule_type ?? mapTypeLabelToScheduleType(r.typeLabel)
        );
        setSingleDayDate(
          parseDateField(
            r.single_day?.date ?? r.calendar_start_date ?? r.single_day_date
          )
        );
        setRangeStartDate(
          parseDateField(
            r.date_range?.start_date ??
              r.calendar_start_date ??
              r.range_start_date ??
              r.start_date
          )
        );
        setRangeEndDate(
          parseDateField(
            r.date_range?.end_date ??
              r.calendar_end_date ??
              r.range_end_date ??
              r.end_date
          )
        );
        setWeekdays(weekdayStateFromPython(r.recurring_weekdays));
        if (
          r.recurring_ends === RECURRING_ENDS_NEVER ||
          r.recurring_open_ended === true
        ) {
          setRecurringEnds(RECURRING_ENDS_NEVER);
          setRecurringUntilDate(undefined);
        } else {
          const until = parseDateField(
            r.recurring_until_date ??
              r.calendar_end_date ??
              r.recurring_end_date ??
              r.recurring?.until_date
          );
          if (until) {
            setRecurringEnds(RECURRING_ENDS_ON_DATE);
            setRecurringUntilDate(until);
          } else {
            setRecurringEnds(RECURRING_ENDS_NEVER);
            setRecurringUntilDate(undefined);
          }
        }
        setTimeMode(
          r.time_mode === TIME_SPECIFIC || r.time_mode === "specific_time"
            ? TIME_SPECIFIC
            : TIME_FULL_DAY
        );
        setStartTime(
          normalizeHHmm(r.start_time ?? r.time_window_start) || "11:00"
        );
        setEndTime(normalizeHHmm(r.end_time ?? r.time_window_end) || "13:00");
      } else {
        setName("");
        setDescription("");
        setScheduleType(SCHEDULE_SINGLE);
        setSingleDayDate(undefined);
        setRangeStartDate(undefined);
        setRangeEndDate(undefined);
        setWeekdays(emptyWeekdays());
        setRecurringEnds(RECURRING_ENDS_NEVER);
        setRecurringUntilDate(undefined);
        setTimeMode(TIME_FULL_DAY);
        setStartTime("11:00");
        setEndTime("13:00");
      }
      setOpenDatePicker(null);
      setSubmitError(null);
    });
    return () => cancelAnimationFrame(id);
  }, [isOpen, initialRecord]);

  const validateSchedule = () => {
    if (scheduleType === SCHEDULE_SINGLE) {
      if (!singleDayDate) return "Select a date for this blackout.";
    }
    if (scheduleType === SCHEDULE_RANGE) {
      if (!rangeStartDate || !rangeEndDate)
        return "Select a start and end date for the date range.";
      if (rangeStartDate > rangeEndDate)
        return "End date must be on or after the start date.";
    }
    if (scheduleType === SCHEDULE_RECURRING) {
      if (!WEEKDAYS.some(({ key }) => weekdays[key]))
        return "Select at least one day of the week.";
      if (recurringEnds === RECURRING_ENDS_ON_DATE && !recurringUntilDate) {
        return "Select an end date for the recurrence, or choose Never.";
      }
    }
    return null;
  };

  const validateTimeWindow = () => {
    if (timeMode === TIME_FULL_DAY) return null;
    if (!startTime || !endTime) return "Select start and end time.";
    if (startTime >= endTime)
      return "End time must be after start time on the same day.";
    return null;
  };

  const handleSave = async (e) => {
    e?.preventDefault?.();
    const trimmedName = name.trim();
    if (!trimmedName) {
      setSubmitError("Blackout name is required.");
      return;
    }
    const scheduleErr = validateSchedule();
    if (scheduleErr) {
      setSubmitError(scheduleErr);
      return;
    }
    const timeErr = validateTimeWindow();
    if (timeErr) {
      setSubmitError(timeErr);
      return;
    }
    setSubmitError(null);
    if (typeof onConfirm !== "function") {
      handleClose(e);
      return;
    }

    const recurringDows = weekdayStateToPython(weekdays);

    /** POST /actions/scheduling/blackouts — body matches upstream /api/scheduling/blackouts */
    const payload = {
      action: BLACKOUT_ACTION_COACH_WIDE,
      name: trimmedName,
      description: description.trim() || "",
      schedule_type: scheduleType,
      time_mode: timeMode,
      /** true = persist overrides and cancel conflicting booked bookings; false = preview only */
      confirm: true,
    };
    if (scheduleType === SCHEDULE_SINGLE) {
      payload.single_day = { date: toYYYYMMDD(singleDayDate) };
    } else if (scheduleType === SCHEDULE_RANGE) {
      payload.date_range = {
        start_date: toYYYYMMDD(rangeStartDate),
        end_date: toYYYYMMDD(rangeEndDate),
      };
    } else if (scheduleType === SCHEDULE_RECURRING) {
      payload.recurring_weekdays = recurringDows;
      if (recurringEnds === RECURRING_ENDS_NEVER) {
        payload.recurring_ends = RECURRING_ENDS_NEVER;
      } else {
        payload.recurring_until_date = toYYYYMMDD(recurringUntilDate);
      }
    }
    if (timeMode === TIME_SPECIFIC) {
      payload.start_time = startTime;
      payload.end_time = endTime;
    }

    setIsSubmitting(true);
    try {
      await Promise.resolve(onConfirm(payload));
      handleClose(e);
    } catch (err) {
      setSubmitError(err?.message ?? "Failed to save blackout");
    } finally {
      setIsSubmitting(false);
    }
  };

  const editId = initialRecord?._id ?? initialRecord?.id;
  const isEdit = Boolean(editId);
  const scheduleValid = validateSchedule() === null;
  const timeValid = validateTimeWindow() === null;
  const canSubmit = Boolean(name.trim()) && scheduleValid && timeValid;

  const dateButtonLabel = (d) => formatDate(d) || "Select";

  const renderDateField = (
    id,
    label,
    date,
    pickerKey,
    pickerRef,
    fieldOnly = false
  ) => {
    const control = (
      <div
        className="create-class-modal-date-dropdown create-blackout-date-field"
        ref={pickerRef}
      >
        <input type="hidden" name={id} value={toYYYYMMDD(date)} />
        <img
          src="/img/new-icons/calendar-ico.svg"
          alt=""
          className="create-class-modal-date-icon"
          aria-hidden
        />
        <button
          type="button"
          id={id}
          className="create-class-modal-date-input create-class-modal-date-trigger"
          onClick={() =>
            setOpenDatePicker((prev) => (prev === pickerKey ? null : pickerKey))
          }
          aria-label={label}
          aria-expanded={openDatePicker === pickerKey}
        >
          {dateButtonLabel(date)}
        </button>
        <img
          src="/img/new-icons/chevron-down.svg"
          alt=""
          className="create-class-modal-date-chevron"
          aria-hidden="true"
        />
      </div>
    );
    if (fieldOnly) {
      return control;
    }
    return (
      <div className="form-group">
        <span className="create-blackout-date-field-caption">{label}</span>
        {control}
      </div>
    );
  };

  if (!isOpen) return null;

  const calendarSelectedDate =
    openDatePicker === "single"
      ? singleDayDate
      : openDatePicker === "rangeStart"
        ? rangeStartDate
        : openDatePicker === "rangeEnd"
          ? rangeEndDate
          : openDatePicker === "recurringUntil"
            ? recurringUntilDate
            : undefined;

  const calendarPortal =
    openDatePicker &&
    typeof document !== "undefined" &&
    createPortal(
      <div
        ref={calendarPopoverRef}
        className="create-class-modal-date-popover create-blackout-date-popover-portal"
        style={{
          position: "fixed",
          top: calendarPopoverPos.top,
          left: calendarPopoverPos.left,
          zIndex: 400,
        }}
      >
        <DayPicker
          mode="single"
          selected={calendarSelectedDate}
          onSelect={(next) => {
            const norm = next ? normalizeCalendarDateForBlackout(next) : next;
            if (openDatePicker === "single") setSingleDayDate(norm);
            else if (openDatePicker === "rangeStart") setRangeStartDate(norm);
            else if (openDatePicker === "rangeEnd") setRangeEndDate(norm);
            else if (openDatePicker === "recurringUntil")
              setRecurringUntilDate(norm);
            setOpenDatePicker(null);
          }}
          defaultMonth={calendarSelectedDate || new Date()}
          showOutsideDays
          components={{ Caption: BlackoutCalendarCaption }}
          modifiersClassNames={{ selected: "my-selected" }}
        />
      </div>,
      document.body
    );

  const selectedWeekdayNames = WEEKDAYS.filter(({ key }) => weekdays[key]).map(
    ({ key }) => WEEKDAY_FULL_NAMES[key]
  );
  const showBlackoutSummary = scheduleValid && timeValid;

  return (
    <div
      className="Modal-Box create-class-modal create-blackout-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-blackout-modal-title"
    >
      <button
        type="button"
        className="overlay"
        onClick={handleClose}
        aria-label="Close modal"
      />
      <div className="box">
        <header>
          <h2 id="create-blackout-modal-title">
            {isEdit ? "Edit Blackout" : "Create Blackout"}
          </h2>
          <button
            type="button"
            onClick={handleClose}
            className="close"
            data-button-type="tertiary"
            data-icon-only="true"
            aria-label="Close"
          >
            <img src="/img/new-icons/red-cross.svg" alt="" />
          </button>
        </header>
        <div className="body">
          <div className="create-class-modal-cols">
            <div className="create-class-modal-section-head">
              <h3 className="create-class-modal-section-title">
                Basis Details
              </h3>
              <p className="create-class-modal-section-desc">
                Define the purpose of this blackout for reference.
              </p>
            </div>
            <div className="create-class-modal-form">
              <div className="form-group">
                <label htmlFor="create-blackout-name">Blackout Name</label>
                <input
                  id="create-blackout-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Weekend Closure, Maintenance, Coach Leave"
                  aria-label="Blackout name"
                  autoComplete="off"
                />
              </div>
              <div className="form-group">
                <label htmlFor="create-blackout-description">
                  Blackout Description
                </label>
                <textarea
                  id="create-blackout-description"
                  rows={4}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Add reason or notes for this blackout (optional)"
                  aria-label="Blackout description"
                />
              </div>
            </div>
          </div>

          <hr className="create-class-modal-separator" />

          <div className="create-class-modal-cols">
            <div className="create-class-modal-section-head">
              <h3 className="create-class-modal-section-title">
                Schedule Type
              </h3>
              <p className="create-class-modal-section-desc">
                Choose whether this blackout applies to a single date, a date
                range, or repeats over time.
              </p>
            </div>
            <div className="create-class-modal-form">
              <fieldset className="create-blackout-schedule-card">
                <legend className="sr-only">Schedule type</legend>
                <div className="create-blackout-schedule-option">
                  <label className="create-blackout-schedule-option-row radio-input">
                    <input
                      type="radio"
                      name="create-blackout-schedule-type"
                      value={SCHEDULE_SINGLE}
                      checked={scheduleType === SCHEDULE_SINGLE}
                      onChange={() => setScheduleType(SCHEDULE_SINGLE)}
                    />
                    <span className="radio" aria-hidden="true" />
                    <span className="create-blackout-schedule-option-copy">
                      <span className="create-blackout-schedule-option-title">
                        Single Day
                      </span>
                      <span className="create-blackout-schedule-option-desc">
                        Block bookings for one specific date.
                      </span>
                    </span>
                  </label>
                  {scheduleType === SCHEDULE_SINGLE && (
                    <div className="create-blackout-schedule-nested">
                      {renderDateField(
                        "create-blackout-single-date",
                        "Select Date",
                        singleDayDate,
                        "single",
                        singlePickerRef
                      )}
                    </div>
                  )}
                </div>

                <div className="create-blackout-schedule-option">
                  <label className="create-blackout-schedule-option-row radio-input">
                    <input
                      type="radio"
                      name="create-blackout-schedule-type"
                      value={SCHEDULE_RANGE}
                      checked={scheduleType === SCHEDULE_RANGE}
                      onChange={() => setScheduleType(SCHEDULE_RANGE)}
                    />
                    <span className="radio" aria-hidden="true" />
                    <span className="create-blackout-schedule-option-copy">
                      <span className="create-blackout-schedule-option-title">
                        Date Range
                      </span>
                      <span className="create-blackout-schedule-option-desc">
                        Block bookings across multiple consecutive dates.
                      </span>
                    </span>
                  </label>
                  {scheduleType === SCHEDULE_RANGE && (
                    <div className="create-blackout-schedule-nested">
                      <div className="create-class-modal-form-two-cols">
                        {renderDateField(
                          "create-blackout-range-start",
                          "Start Date",
                          rangeStartDate,
                          "rangeStart",
                          rangeStartPickerRef
                        )}
                        {renderDateField(
                          "create-blackout-range-end",
                          "End Date",
                          rangeEndDate,
                          "rangeEnd",
                          rangeEndPickerRef
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div className="create-blackout-schedule-option">
                  <label className="create-blackout-schedule-option-row radio-input">
                    <input
                      type="radio"
                      name="create-blackout-schedule-type"
                      value={SCHEDULE_RECURRING}
                      checked={scheduleType === SCHEDULE_RECURRING}
                      onChange={() => setScheduleType(SCHEDULE_RECURRING)}
                    />
                    <span className="radio" aria-hidden="true" />
                    <span className="create-blackout-schedule-option-copy">
                      <span className="create-blackout-schedule-option-title">
                        Recurring
                      </span>
                      <span className="create-blackout-schedule-option-desc">
                        Choose the days of the week when this blackout should
                        repeat.
                      </span>
                    </span>
                  </label>
                  {scheduleType === SCHEDULE_RECURRING && (
                    <div className="create-blackout-schedule-nested">
                      <fieldset className="create-blackout-weekdays">
                        <legend className="sr-only">Days of the week</legend>
                        {WEEKDAYS.map(({ key, label }) => (
                          <label key={key} className="checkbox-input">
                            <input
                              type="checkbox"
                              checked={weekdays[key]}
                              onChange={() => toggleWeekday(key)}
                            />
                            <span className="checkbox" aria-hidden="true" />
                            <span className="text">{label}</span>
                          </label>
                        ))}
                      </fieldset>
                      <div className="create-blackout-recurring-ends">
                        <p className="create-blackout-recurring-ends-title">
                          Recurring ends
                        </p>
                        <fieldset className="create-blackout-recurring-ends-radios">
                          <legend className="sr-only">Recurring ends</legend>
                          <label className="radio-input">
                            <input
                              type="radio"
                              name="create-blackout-recurring-ends"
                              checked={recurringEnds === RECURRING_ENDS_NEVER}
                              onChange={() =>
                                setRecurringEnds(RECURRING_ENDS_NEVER)
                              }
                            />
                            <span className="radio" aria-hidden="true" />
                            <span>Never</span>
                          </label>
                          <div className="create-blackout-recurring-on-date-row">
                            <label className="radio-input">
                              <input
                                type="radio"
                                name="create-blackout-recurring-ends"
                                checked={
                                  recurringEnds === RECURRING_ENDS_ON_DATE
                                }
                                onChange={() =>
                                  setRecurringEnds(RECURRING_ENDS_ON_DATE)
                                }
                              />
                              <span className="radio" aria-hidden="true" />
                              <span>On Date</span>
                            </label>
                            {recurringEnds === RECURRING_ENDS_ON_DATE && (
                              <div className="create-blackout-recurring-end-date-inline">
                                <p className="create-blackout-recurring-end-date-label">
                                  End Date
                                </p>
                                {renderDateField(
                                  "create-blackout-recurring-end-date",
                                  "End Date",
                                  recurringUntilDate,
                                  "recurringUntil",
                                  recurringUntilPickerRef,
                                  true
                                )}
                              </div>
                            )}
                          </div>
                        </fieldset>
                      </div>
                    </div>
                  )}
                </div>
              </fieldset>
            </div>
          </div>

          <hr className="create-class-modal-separator" />

          <div className="create-class-modal-cols">
            <div className="create-class-modal-section-head">
              <h3 className="create-class-modal-section-title">Set Time</h3>
              <p className="create-class-modal-section-desc">
                Specify whether this blackout blocks the full day or a selected
                time range.
              </p>
            </div>
            <div className="create-class-modal-form">
              <fieldset className="create-blackout-schedule-card create-blackout-set-time-card">
                <legend className="sr-only">Time mode</legend>
                <div className="create-blackout-schedule-option">
                  <label className="create-blackout-schedule-option-row radio-input">
                    <input
                      type="radio"
                      name="create-blackout-time-mode"
                      value={TIME_FULL_DAY}
                      checked={timeMode === TIME_FULL_DAY}
                      onChange={() => setTimeMode(TIME_FULL_DAY)}
                    />
                    <span className="radio" aria-hidden="true" />
                    <span className="create-blackout-schedule-option-copy">
                      <span className="create-blackout-schedule-option-title">
                        Full Day
                      </span>
                      <span className="create-blackout-schedule-option-desc">
                        Block bookings for the entire day.
                      </span>
                    </span>
                  </label>
                </div>
                <div className="create-blackout-schedule-option">
                  <label className="create-blackout-schedule-option-row radio-input">
                    <input
                      type="radio"
                      name="create-blackout-time-mode"
                      value={TIME_SPECIFIC}
                      checked={timeMode === TIME_SPECIFIC}
                      onChange={() => setTimeMode(TIME_SPECIFIC)}
                    />
                    <span className="radio" aria-hidden="true" />
                    <span className="create-blackout-schedule-option-copy">
                      <span className="create-blackout-schedule-option-title">
                        Specific Time
                      </span>
                      <span className="create-blackout-schedule-option-desc">
                        Block bookings only during a selected time range.
                      </span>
                    </span>
                  </label>
                  {timeMode === TIME_SPECIFIC && (
                    <div className="create-blackout-schedule-nested">
                      <div className="create-class-modal-form-two-cols">
                        <div className="form-group">
                          <span className="create-blackout-date-field-caption">
                            Start Time
                          </span>
                          <div className="create-class-modal-select-wrapper">
                            <select
                              id="create-blackout-start-time"
                              value={startTime}
                              onChange={(e) => setStartTime(e.target.value)}
                              aria-label="Start time"
                            >
                              {BLACKOUT_TIME_OPTIONS.map(({ value, label }) => (
                                <option key={value} value={value}>
                                  {label}
                                </option>
                              ))}
                            </select>
                            <img
                              src="/img/new-icons/chevron-down.svg"
                              alt=""
                              className="create-class-modal-select-chevron"
                              aria-hidden
                            />
                          </div>
                        </div>
                        <div className="form-group">
                          <span className="create-blackout-date-field-caption">
                            End Time
                          </span>
                          <div className="create-class-modal-select-wrapper">
                            <select
                              id="create-blackout-end-time"
                              value={endTime}
                              onChange={(e) => setEndTime(e.target.value)}
                              aria-label="End time"
                            >
                              {BLACKOUT_TIME_OPTIONS.map(({ value, label }) => (
                                <option key={value} value={value}>
                                  {label}
                                </option>
                              ))}
                            </select>
                            <img
                              src="/img/new-icons/chevron-down.svg"
                              alt=""
                              className="create-class-modal-select-chevron"
                              aria-hidden
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </fieldset>
            </div>
          </div>

          {showBlackoutSummary && (
            <div className="create-blackout-summary-warning" aria-live="polite">
              <img
                src="/img/new-icons/info-circle-red.svg"
                alt=""
                className="create-blackout-summary-warning-icon"
                aria-hidden
              />
              <p className="create-blackout-summary-warning-text">
                You are blocking{" "}
                {scheduleType === SCHEDULE_RECURRING ? (
                  <strong className="create-blackout-summary-strong">
                    every {joinEnglishList(selectedWeekdayNames)}
                  </strong>
                ) : scheduleType === SCHEDULE_SINGLE ? (
                  <strong className="create-blackout-summary-strong">
                    {formatDate(singleDayDate)}
                  </strong>
                ) : (
                  <strong className="create-blackout-summary-strong">
                    from {formatDate(rangeStartDate)} to{" "}
                    {formatDate(rangeEndDate)}
                  </strong>
                )}
                {timeMode === TIME_SPECIFIC ? (
                  <>
                    {" "}
                    from{" "}
                    <strong className="create-blackout-summary-strong">
                      {formatHHmm12(startTime)} – {formatHHmm12(endTime)}
                    </strong>
                  </>
                ) : (
                  <>
                    {" "}
                    for the{" "}
                    <strong className="create-blackout-summary-strong">
                      full day
                    </strong>
                  </>
                )}
                {scheduleType === SCHEDULE_RECURRING &&
                  recurringEnds === RECURRING_ENDS_ON_DATE &&
                  recurringUntilDate && (
                    <>
                      {" "}
                      through{" "}
                      <strong className="create-blackout-summary-strong">
                        {formatDate(recurringUntilDate)}
                      </strong>
                    </>
                  )}
                {scheduleType === SCHEDULE_RECURRING &&
                  recurringEnds === RECURRING_ENDS_NEVER && (
                    <> with no end date</>
                  )}
                .
              </p>
            </div>
          )}

          {submitError && (
            <p className="create-class-modal-error" role="alert">
              {submitError}
            </p>
          )}
          <footer className="scheduling-classes-modal-footer">
            <button
              type="button"
              data-button-type="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="button"
              data-button-type="primary"
              onClick={handleSave}
              disabled={isSubmitting || !canSubmit}
            >
              {isSubmitting ? "Saving…" : isEdit ? "Save changes" : "Save"}
            </button>
          </footer>
        </div>
      </div>
      {calendarPortal}
    </div>
  );
};

export default CreateBlackoutModal;
